import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  useController,
} from "react-hook-form"
import { CustomHelperText } from "ui"
import ActiveCheckbox from "ui/icons/ActiveCheckbox"
import CheckboxIcon from "ui/icons/CheckboxIcon"
import {
  CheckboxRow,
  StyledDivider,
  StyledHeader,
  StyledInputLabel,
  StyledInputLabelNumber,
  StyledSelect,
  StyledSelectMenuProps,
} from "./styles"

export function formatDate(date?: Date, time?: string) {
  if (!date) {
    return undefined
  }
  const eventDate = new Date(date)
  if (time) {
    const [hours, minutes] = time.split(":")
    eventDate.setHours(parseInt(hours))
    eventDate.setMinutes(parseInt(minutes))
  }

  return new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
}

// Helper function to render label with optional required asterisk
const renderLabel = (label: string, required?: boolean) => (
  <>
    <span style={{ color: "#051711" }}>{label}</span>
    {required && <span style={{ color: "#E00700" }}>*</span>}
  </>
)

export const notAfterNowIfToday = (value?: string, lostDate?: string) => {
  if (!value || !lostDate) return true
  const inputDate = new Date(lostDate)
  const now = new Date()
  if (inputDate.toDateString() === now.toDateString()) {
    const [hours, minutes] = value.split(":").map(Number)
    if (
      hours > now.getHours() ||
      (hours === now.getHours() && minutes > now.getMinutes())
    ) {
      return false
    }
  }
  return true
}

export const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY

if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

// Hook for managing focus on form validation errors
export const useFormErrorFocus = <T extends FieldValues>(
  errors: FieldErrors<T>,
  isSubmitted: boolean,
  isValid: boolean,
) => {
  useEffect(() => {
    if (isSubmitted && !isValid && Object.keys(errors).length > 0) {
      // Get the first field with an error
      const firstErrorField = Object.keys(errors)[0]
      // Focus on the first error field
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`,
      ) as HTMLElement
      if (errorElement) {
        errorElement.focus()
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [errors, isSubmitted, isValid])
}

interface CustomTextFieldProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  type?: string
  multiline?: boolean
  rows?: number
  fullWidth?: boolean
  isSubmitted?: boolean
  slotProps?: any
  sx?: any
  errors?: any
  fontSize?: number
  maxLength?: number
  renderHelperText?: (error: boolean, message?: string) => React.ReactNode
}

export const CustomTextField = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  type = "text",
  multiline = false,
  rows,
  fullWidth = true,
  isSubmitted = false,
  slotProps,
  sx,
  errors,
  fontSize,
  maxLength,
  renderHelperText,
}: CustomTextFieldProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div data-field={name} style={{ width: "100%" }}>
          <TextField
            {...field}
            label={renderLabel(label, required)}
            type={type}
            variant="filled"
            error={!!errors?.[name]}
            aria-label={label}
            helperText={
              renderHelperText
                ? renderHelperText(!!errors?.[name], errors?.[name]?.message)
                : (
                  <CustomHelperText
                    render={isSubmitted}
                    error={!!errors?.[name]}
                    text={errors?.[name]?.message}
                  />
                )
            }
            multiline={multiline}
            rows={rows}
            slotProps={{
              ...slotProps,
              htmlInput: {
                "aria-required": required,
                ...(maxLength != null && { maxLength }),
              },
              input: {
                sx: {
                  fontSize,
                  "::placeholder": {
                    fontSize: 1,
                  },
                },
              },

              inputLabel: {
                display: "none !important",
                sx: {
                  fontSize,
                  "::placeholder": {
                    fontSize: 1,
                  },
                },
              },
            }}
            sx={{
              marginBlockEnd: "24px",
              "& .MuiFormLabel-root": {
                fontSize,
              },
              ...sx,
            }}
            fullWidth={fullWidth}
          />
        </div>
      )}
    />
  )
}

interface CustomSelectProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  fullWidth?: boolean
  isSubmitted?: boolean
  options: { value: number | string; label: string }[]
  sx?: any
  errors?: any
  fontSize?: number
  IconComponent?: React.ElementType
  renderHelperText?: (error: boolean, message?: string) => React.ReactNode
  menuPaperSx?: Record<string, unknown>
}

export const CustomSelect = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  fullWidth = true,
  isSubmitted = false,
  options,
  sx,
  errors,
  fontSize,
  IconComponent,
  renderHelperText,
  menuPaperSx,
}: CustomSelectProps<T>) => {
  const labelId = `${name}-label`
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth={fullWidth}
          error={!!errors?.[name]}
          data-field={name}
          sx={{
            marginBlockEnd: "24px",
            ...sx,
          }}
        >
          <StyledInputLabel
            id={labelId}
            htmlFor={name}
            sx={{
              ...(fontSize && { fontSize: `${fontSize}px !important` }),
            }}
          >
            {renderLabel(label, required)}
          </StyledInputLabel>
          <StyledSelect
            {...field}
            labelId={labelId}
            id={name}
            variant="filled"
            MenuProps={
              menuPaperSx
                ? {
                    ...StyledSelectMenuProps,
                    PaperProps: {
                      ...StyledSelectMenuProps.PaperProps,
                      sx: {
                        ...StyledSelectMenuProps.PaperProps.sx,
                        ...menuPaperSx,
                      },
                    },
                  }
                : StyledSelectMenuProps
            }
            aria-label={label}
            aria-required={required}
            {...(IconComponent && { IconComponent })}
            sx={{
              ...(fontSize && {
                fontSize: `${fontSize}px`,

                "& .MuiSelect-select": {
                  fontSize: `${fontSize}px`,
                },
              }),
            }}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  ...(fontSize && { fontSize: `${fontSize}px` }),
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
          {renderHelperText
            ? renderHelperText(!!errors?.[name], errors?.[name]?.message)
            : (
              <CustomHelperText
                render={isSubmitted}
                error={!!errors?.[name]}
                text={errors?.[name]?.message}
              />
            )}
        </FormControl>
      )}
    />
  )
}

interface Option {
  value: number | string
  label: string
}

interface CustomAutocompleteProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  fullWidth?: boolean
  isSubmitted?: boolean
  options: Option[]
  sx?: any
  errors?: any
  onInputChange?: (value: string) => void
  loading?: boolean
  loadingText: string
  noOptionsText: string
  startTypingText: string
  disabled?: boolean
}

export const CustomAutocomplete = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  fullWidth = true,
  isSubmitted = false,
  options,
  sx,
  errors,
  onInputChange,
  loading = false,
  loadingText,
  noOptionsText,
  startTypingText,
  disabled = false,
}: CustomAutocompleteProps<T>) => {
  const [inputValue, setInputValue] = useState("")

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <FormControl
            fullWidth={fullWidth}
            error={!!errors?.[name]}
            data-field={name}
            sx={{
              marginBlockEnd: "24px",
              ...sx,
            }}
          >
            <Autocomplete<Option, false, false, false>
              id={`autocomplete-${name}`}
              options={options}
              loading={loading}
              disabled={disabled}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                String(option.value) === String(value.value)
              }
              value={
                options.find(
                  (opt) => String(opt.value) === String(field.value),
                ) || null
              }
              autoHighlight={false}
              autoSelect={false}
              onChange={(_, newValue) => {
                field.onChange(newValue ? newValue.value : null)
              }}
              onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue)
                if (reason === "input") {
                  onInputChange?.(newInputValue)
                }
              }}
              filterOptions={(options, state) => options}
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  key={option.value}
                  style={{ height: "67px", whiteSpace: "normal" }}
                >
                  {option.label}
                </MenuItem>
              )}
              loadingText={loadingText}
              noOptionsText={
                inputValue === "" ? startTypingText : noOptionsText
              }
              slotProps={{
                paper: {
                  sx: {
                    ...StyledSelectMenuProps.PaperProps.sx,
                    overflowY: "hidden",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={renderLabel(label, required)}
                  variant="filled"
                  error={!!errors?.[name]}
                  disabled={disabled}
                  aria-label={label}
                  aria-required={required}
                  inputProps={{
                    ...params.inputProps,
                    "aria-required": required,
                  }}
                  helperText={
                    <CustomHelperText
                      render={isSubmitted}
                      error={!!errors?.[name]}
                      text={errors?.[name]?.message}
                    />
                  }
                />
              )}
            />
          </FormControl>
        )
      }}
    />
  )
}

interface MultilineCustomTextFieldProps<T extends FieldValues>
  extends Omit<CustomTextFieldProps<T>, "multiline" | "rows"> {
  minRows?: number
  maxRows?: number
}

export const MultilineCustomTextField = <T extends FieldValues>({
  minRows = 3,
  maxRows = 6,
  ...props
}: MultilineCustomTextFieldProps<T>) => {
  const [count, setCount] = useState(0)
  const { field } = useController({ name: props.name, control: props.control })
  const maxLength = 3000

  useEffect(() => {
    setCount(field.value?.length || 0)
  }, [field.value])

  return (
    <div data-field={props.name}>
      <CustomTextField
        {...props}
        multiline
        rows={6}
        sx={{ maxWidth: "736px", marginBlockEnd: "0" }}
        slotProps={{
          input: {
            style: {
              resize: "vertical",
              fontSize: "16px",
              marginBlockEnd: "0",
            },
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "right",
          maxWidth: "736px",
          marginTop: "4px",
        }}
      >
        {count}/{maxLength}
      </Typography>
    </div>
  )
}

interface DateControllerProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  isSubmitted?: boolean
  errors?: any
  sx?: any
  midnightText?: string
  okButtonLabel: string
  cancelButtonLabel: string
  toolbarTitle: string
  locale: "he" | "en" | "ar" | "ru"
}

export const DateController = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  isSubmitted = false,
  errors,
  sx,
  midnightText,
  okButtonLabel,
  cancelButtonLabel,
  toolbarTitle,
  locale,
}: DateControllerProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <div
            data-field={name}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
              width: "100%",
            }}
          >
            <DatePicker
              {...field}
              label={renderLabel(label, required)}
              disableFuture
              // minDate={dayjs().subtract(3, "month")}
              localeText={{
                okButtonLabel: okButtonLabel,
                cancelButtonLabel: cancelButtonLabel,
                toolbarTitle: toolbarTitle,
              }}
              slotProps={{
                textField: {
                  variant: "filled",
                  fullWidth: true,
                  error: !!errors?.[name],
                  autoComplete: "bday",
                  "aria-label": label,
                  "aria-required": required,
                  helperText: (
                    <CustomHelperText
                      render={isSubmitted}
                      error={!!errors?.[name]}
                      text={errors?.[name]?.message}
                    />
                  ),
                  sx: {
                    ...sx,
                  },
                },
              }}
            />
            {/* Mirror arrow icons for RTL locales (he, ar) */}
            {(locale === "he" || locale === "ar") && (
              <style>
                {`
                /* Target both previous and next month buttons icons */
                :root .MuiPickersArrowSwitcher-button svg { 
                  transform: scaleX(-1); 
                }
                `}
              </style>
            )}
            <Typography fontSize={14} variant="body4">
              {midnightText}
            </Typography>
          </div>
        </LocalizationProvider>
      )}
    />
  )
}

interface NumbersControllerProps<T extends FieldValues> {
  prefixName: Path<T>
  phoneName: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  isSubmitted?: boolean
  errors?: any
  sx?: any
  prefixOptions?: { value: string; label: string }[]
  locale: "he" | "en" | "ar" | "ru"
}

export const prefixOptionsArray = [
  { value: "050", label: "050" },
  { value: "051", label: "051" },
  { value: "052", label: "052" },
  { value: "053", label: "053" },
  { value: "054", label: "054" },
  { value: "055", label: "055" },
  { value: "056", label: "056" },
  { value: "058", label: "058" },
  { value: "059", label: "059" },
]

export const NumbersController = <T extends FieldValues>({
  prefixName,
  phoneName,
  control,
  label,
  required = false,
  isSubmitted = false,
  errors,
  sx,
  prefixOptions = prefixOptionsArray,
  locale,
}: NumbersControllerProps<T>) => {
  const hasError = !!errors?.[phoneName]

  return (
    <div
      data-field={phoneName}
      style={{
        display: "flex",
        flexDirection:
          locale === "he" || locale === "ar" ? "row" : "row-reverse",
        width: "100%",
      }}
    >
      <Controller
        name={phoneName}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={renderLabel(label, required)}
            type="text"
            variant="filled"
            autoComplete="tel-local"
            error={hasError}
            aria-label={label}
            aria-required={required}
            helperText={
              <CustomHelperText
                render={isSubmitted}
                error={hasError}
                text={errors?.[phoneName]?.message}
              />
            }
            slotProps={{
              htmlInput: {
                "aria-required": required,
              },
            }}
            sx={{
              marginBlockEnd: "24px",
              flex: 1,
              "& .MuiInputBase-root": {
                ...(locale === "he" || locale === "ar"
                  ? {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }),
              },
              ...sx,
            }}
            fullWidth
          />
        )}
      />
      <Controller
        name={prefixName}
        control={control}
        render={({ field }) => (
          <FormControl
            error={!!errors?.[prefixName]}
            data-field={name}
            sx={{
              marginBlockEnd: "24px",
              ...sx,
            }}
          >
            <StyledInputLabelNumber>
              <Typography variant="body3">
                {renderLabel("קידומת", required)}
              </Typography>
            </StyledInputLabelNumber>
            <Select
              {...field}
              onChange={(e) => {
                field.onChange(e)
              }}
              defaultValue={prefixOptions[0].value}
              error={hasError}
              aria-label="קידומת"
              aria-required={required}
              slotProps={{
                input: {
                  "aria-label": "קידומת",
                },
                root: {
                  "aria-label": "קידומת",
                },
              }}
              inputProps={{
                "aria-label": "קידומת",
                "aria-required": required,
              }}
              sx={{
                minwidth: "84px",
                width: "auto",
                height: "56px",
                ...(locale === "he" || locale === "ar"
                  ? {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }
                  : {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }),
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError ? "#E00700" : undefined,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError ? "#E00700" : undefined,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError ? "#E00700" : undefined,
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    width: "85px",
                    maxHeight: "200px",
                    boxShadow: "0px 4px 12px 0px #00000029",
                    border: "1px solid #6F747D",
                    borderRadius: "8px",
                    paddingLeft: "0px",
                    paddingRight: "0px",
                  },
                },
              }}
            >
              {prefixOptions.map((option) => (
                <MenuItem
                  style={{
                    padding: "16px",
                  }}
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
    </div>
  )
}

export const Header = ({ title }: { title?: string }) => {
  const tInner = useTranslations("InnerPages")
  return (
    <StyledHeader>
      <Typography variant="h4">{title || tInner("personalDetails")}</Typography>
      <StyledDivider />
      <Typography variant="body2">
        {tInner("mandatoryFieldWithAsterisk")}
        <span style={{ color: "#E00700" }}>*</span>
      </Typography>
    </StyledHeader>
  )
}

export const isFieldRequired = (schema: any, fieldName: string): boolean => {
  const field = schema.describe().fields[fieldName] as any

  if (field.optional) {
    return false
  }

  if (field.type === "date") {
    return !field.nullable
  }

  const hasRequiredTest =
    field.tests?.some(
      (item: any) => item.name === "required" || item.name === "defined",
    ) ?? false

  return hasRequiredTest || (!field.nullable && !field.optional)
}

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
`
interface CustomCheckboxProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  errors: any
  isMultiLine?: boolean
}

export const CustomCheckbox = <T extends FieldValues>({
  name,
  control,
  label,
  errors,
  isMultiLine,
}: CustomCheckboxProps<T>) => {
  const checkboxId = `checkbox-${name}`
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <CheckboxRow isMultiLine={isMultiLine}>
            <FormControlLabel
              htmlFor={checkboxId}
              control={
                <Checkbox
                  {...field}
                  id={checkboxId}
                  icon={<CheckboxIcon isError={!!errors[name]} />}
                  checkedIcon={<ActiveCheckbox />}
                  checked={!!field.value} // Ensure checked is boolean
                  sx={{
                    marginRight: "-8px",
                    paddingLeft: 0,
                    ":hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e.target.checked)
                  }}
                  inputProps={{
                    "aria-label": label.replace(/<[^>]*>/g, ""), // Remove HTML tags for aria-label
                  }}
                />
              }
              label={
                <StyledTypography
                  variant="body3"
                  component={"div"}
                  style={{ maxWidth: 675, textAlign: "right" }}
                  dangerouslySetInnerHTML={{ __html: label }}
                />
              }
              sx={{
                gap: "8px",
                marginLeft: 0,
              }}
            />
          </CheckboxRow>
        )
      }}
    />
  )
}
